import { toPairs } from "lodash";
import * as Sentry from "@sentry/angular";

import { sntLog } from "src/app/common/logger";

const logger = sntLog("ErrorReporting");
/**
 * Sentry reports non-Error values as "Object captured as exception with keys: …"
 * (e.g. fetch Response, or Angular HttpErrorResponse which does not extend Error).
 * Always pass a real Error for readable titles and stack behavior.
 */
export function toReportableError(ex: unknown): Error {
  if (ex instanceof Error) {
    return ex;
  }

  if (typeof Response !== "undefined" && ex instanceof Response) {
    return new Error(`HTTP ${ex.status} ${ex.statusText}: ${ex.url}`);
  }

  if (ex !== null && typeof ex === "object") {
    const o = ex as Record<string, unknown>;

    if (o.name === "HttpErrorResponse" && typeof o.message === "string") {
      return new Error(o.message);
    }

    if (typeof o.status === "number" && typeof o.url === "string") {
      const statusText: string =
        typeof o.statusText === "string" ? o.statusText : "";

      return new Error(`HTTP ${o.status} ${statusText}: ${o.url}`);
    }
  }

  if (typeof ex === "string") {
    return new Error(ex);
  }

  try {
    return new Error(JSON.stringify(ex));
  } catch {
    return new Error("Unknown non-Error exception");
  }
}

export function setUserForErrorReporting({
  id,
  username,
  email
}: {
  id: string;
  username: string;
  email: string;
}): void {
  Sentry.setUser({
    id,
    username,
    email
  });
}

export function reportError(ex: unknown): void {
  const normalized: Error = toReportableError(ex);
  const usedNormalization: boolean = !(
    ex instanceof Error && ex === normalized
  );

  if (usedNormalization) {
    Sentry.captureException(normalized, {
      extra: {
        originalExceptionKind:
          typeof Response !== "undefined" && ex instanceof Response
            ? "fetch Response"
            : ex !== null && typeof ex === "object" && "name" in ex
              ? String((ex as { name: unknown }).name)
              : typeof ex
      }
    });

    return;
  }

  Sentry.captureException(normalized);
}

export function reportErrorWithExtraScope(
  ex: unknown,
  extraData: Record<string, unknown>
): void {
  const normalized: Error = toReportableError(ex);

  Sentry.captureException(normalized, {
    extra: extraData
  });
}

export function reportMessageWithExtraScope(
  message: string,
  extraData: any
): void {
  Sentry.withScope((scope: any) => {
    toPairs(scope).forEach(([key, value]) => {
      scope.setExtra(key, value);
    });

    Sentry.captureMessage(message);
  });

  logger.error(message);
}
